import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  message,
  Space,
  Popconfirm,
  Typography,
  Card,
  Tag,
  Select,
  Badge,
  Divider,
  Row,
  Col,
  Avatar,
  Upload,
  Image,
  Descriptions,
  Tabs,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  UserOutlined,
  UploadOutlined,
  CameraOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const UserMaintenance = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [userDetailVisible, setUserDetailVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const printRef = useRef();

  const API_BASE_URL = "https://techcrew.pk/ulc/";

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchText, selectedRole, selectedStatus]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Users.php`);

      if (data.status && data.status.code === 200) {
        setUsers(
          data.data.map((user) => ({
            ...user,
            profile_picture: user.profile_picture
              ? `${API_BASE_URL}${user.profile_picture}`
              : null,
          }))
        );
      } else {
        message.error(data.status?.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("API Error:", error);
      message.error(
        error.response?.data?.message || "Failed to connect to server"
      );
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.email.toLowerCase().includes(searchLower) ||
          item.phone.toLowerCase().includes(searchLower)
      );
    }

    if (selectedRole) {
      filtered = filtered.filter((item) => item.role === selectedRole);
    }

    if (selectedStatus !== null) {
      filtered = filtered.filter(
        (item) => item.is_verified.toString() === selectedStatus
      );
    }

    setFilteredUsers(filtered);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      formData.append("role", values.role);
      formData.append("is_verified", values.is_verified);
      formData.append("language_pref", values.language_pref || "en");

      if (values.password) {
        formData.append("password", values.password);
      }

      if (fileList.length > 0) {
        formData.append("profile_picture", fileList[0].originFileObj);
      }

      if (editingId) {
        formData.append("user_id", editingId);
      }

      const url = editingId
        ? `${API_BASE_URL}Update_User.php`
        : `${API_BASE_URL}Add_User.php`;

      const { data } = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (data.status.code === 200) {
        message.success(data.status.message);
        setModalVisible(false);
        setFileList([]);
        fetchUsers();
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error(error.message || "Error saving user");
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}Delete_User.php`, {
        user_id: id,
      });

      if (data.status.code === 200) {
        message.success("User deleted successfully");
        fetchUsers();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      message.error(error.message || "Deletion failed");
    }
  };

  const handlePrint = () => {
    const printContent = `
    <div style="padding: 30px; font-family: Arial, sans-serif; font-size: 12px;">
      <!-- Header Section -->
      <div style="text-align: center; margin-bottom: 20px;">
        <h1 style="margin: 0; font-size: 24px; font-weight: bold;">UNITED LIMITED COMPANY</h1>
        <p style="font-size: 12px; margin: 5px 0 0 0;">
          8074, Al Sahaba Street, Al Yarmuk District, Riyadh, 13243 Kingdom of Saudi Arabia
        </p>
        <Divider style="margin: 10px 0;" />
        <h2 style="margin: 10px 0; font-size: 18px;">Users Report</h2>
        <p style="margin: 5px 0;">
          <strong>Report Date:</strong> ${new Date().toLocaleDateString()} | 
          <strong>Total Users:</strong> ${filteredUsers.length}
        </p>
      </div>

      <!-- Users Table -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #f0f0f0;">
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">User</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Contact</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Role</th>
            <th style="padding: 8px; text-align: center; border: 1px solid #ddd;">Status</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Last Login</th>
          </tr>
        </thead>
        <tbody>
          ${filteredUsers
            .map(
              (user) => `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">
                <div style="font-weight: 500;">${user.name}</div>
                <div style="font-size: 11px; color: #666;">${user.email}</div>
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                user.phone || "-"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                <span style="
                  display: inline-block;
                  padding: 2px 8px;
                  border-radius: 12px;
                  font-size: 11px;
                  font-weight: 500;
                  background-color: ${
                    user.role === "admin"
                      ? "#fff2f0"
                      : user.role === "supplier"
                      ? "#fff7e6"
                      : "#f0f9ff"
                  };
                  color: ${
                    user.role === "admin"
                      ? "#f5222d"
                      : user.role === "supplier"
                      ? "#fa8c16"
                      : "#1890ff"
                  };
                ">
                  ${user.role}
                </span>
              </td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">
                <span style="
                  display: inline-block;
                  padding: 2px 8px;
                  border-radius: 12px;
                  font-size: 11px;
                  font-weight: 500;
                  background-color: ${user.is_verified ? "#f6ffed" : "#fff1f0"};
                  color: ${user.is_verified ? "#52c41a" : "#f5222d"};
                ">
                  ${user.is_verified ? "Verified" : "Not Verified"}
                </span>
              </td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                ${
                  user.last_login
                    ? moment(user.last_login).format("lll")
                    : "Never"
                }
              </td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <!-- Summary -->
      <div style="margin-top: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Total Users:</strong>
          <span>${filteredUsers.length}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Verified Users:</strong>
          <span>${filteredUsers.filter((u) => u.is_verified).length}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Admins:</strong>
          <span>${filteredUsers.filter((u) => u.role === "admin").length}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Suppier:</strong>
          <span>${
            filteredUsers.filter((u) => u.role === "supplier").length
          }</span>
        </div>
      </div>

      <!-- Footer -->
      <Divider style="margin: 20px 0 10px 0;" />
      <div style="text-align: center; font-size: 11px; color: #666;">
        <p>Generated by UNITED LIMITED COMPANY User Management System</p>
        <p>Printed on ${new Date().toLocaleString()}</p>
      </div>
    </div>
  `;

    // Create an iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
    <html>
      <head>
        <title>Users Report - ${new Date().toLocaleDateString()}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap" rel="stylesheet">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap');

          @media print {
            @page {
              margin: 10mm;
              size: auto;
            }
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-family: Arial, sans-serif;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            th, td {
              padding: 8px;
              text-align: left;
              border: 1px solid #ddd;
            }
            .divider {
              border-top: 1px solid #ddd;
              margin: 10px 0;
            }
          }
        </style>
      </head>
      <body>${printContent}</body>
    </html>
  `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
    }
    return isImage && isLt2M;
  };

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserDetailVisible(true);
  };

  const columns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => handleViewUser(record)}
        >
          <Avatar
            src={record.profile_picture}
            icon={<UserOutlined />}
            style={{
              marginRight: 8,
              width: 40,
              height: 40,
              backgroundColor: record.profile_picture
                ? "transparent"
                : "#1890ff",
            }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: 12, color: "#666" }}>{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag
          color={
            role === "admin" ? "red" : role === "supplier" ? "orange" : "blue"
          }
          style={{
            textTransform: "capitalize",
            fontWeight: 500,
          }}
        >
          {role}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_verified",
      key: "status",
      render: (verified) => (
        <Badge
          status={verified ? "success" : "error"}
          text={
            <span style={{ fontWeight: 500 }}>
              {verified ? "Verified" : "Not Verified"}
            </span>
          }
        />
      ),
    },

    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      render: (date) => (
        <Tag color={date ? "green" : "default"}>
          {date ? moment(date).format("lll") : "Never"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setEditingId(record.user_id);
              form.setFieldsValue({
                name: record.name,
                email: record.email,
                phone: record.phone,
                role: record.role,
                is_verified: record.is_verified,
                language_pref: record.language_pref,
              });
              setFileList(
                record.profile_picture
                  ? [
                      {
                        uid: "-1",
                        name: "profile.jpg",
                        status: "done",
                        url: record.profile_picture,
                      },
                    ]
                  : []
              );
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete this user?"
            onConfirm={(e) => {
              e.stopPropagation();
              handleDelete(record.user_id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            User Management
          </Title>
        }
        bordered={false}
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              style={{ background: "#f0f2f5", border: "none" }}
            >
              Print Users
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setEditingId(null);
                setFileList([]);
                setModalVisible(true);
              }}
            >
              Add User
            </Button>
          </Space>
        }
        headStyle={{
          borderBottom: "1px solid #f0f0f0",
          padding: "0 24px",
        }}
        bodyStyle={{
          padding: "24px",
        }}
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ borderRadius: 6 }}
            />
          </Col>
          <Col span={8}>
            <Select
              style={{ width: "100%", borderRadius: 6 }}
              placeholder="Filter by role"
              allowClear
              value={selectedRole}
              onChange={(value) => setSelectedRole(value)}
              suffixIcon={<UserOutlined />}
            >
              <Option value="admin">Admin</Option>
              <Option value="renter">Renter</Option>
              <Option value="supplier">Supplier</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Select
              style={{ width: "100%", borderRadius: 6 }}
              placeholder="Filter by status"
              allowClear
              value={selectedStatus}
              onChange={(value) => setSelectedStatus(value)}
              suffixIcon={<InfoCircleOutlined />}
            >
              <Option value="1">Verified</Option>
              <Option value="0">Not Verified</Option>
            </Select>
          </Col>
        </Row>

        <div ref={printRef}>
          <Table
            columns={columns}
            dataSource={filteredUsers}
            rowKey="user_id"
            loading={loading}
            scroll={{ x: true }}
            bordered={false}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} users`,
            }}
            style={{
              borderRadius: 8,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
            }}
            onRow={(record) => {
              return {
                onClick: () => handleViewUser(record),
              };
            }}
          />
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            {editingId ? (
              <>
                <EditOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                <span>Edit User</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                <span>Add New User</span>
              </>
            )}
          </div>
        }
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setFileList([]);
        }}
        width={700}
        destroyOnClose
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Tabs defaultActiveKey="1">
            <TabPane tab="Basic Info" key="1">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Full Name"
                    rules={[
                      { required: true, message: "Please enter full name" },
                    ]}
                  >
                    <Input placeholder="Enter full name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: "Please enter email" },
                      { type: "email", message: "Please enter valid email" },
                    ]}
                  >
                    <Input placeholder="Enter email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true, message: "Please enter phone" }]}
                  >
                    <Input placeholder="Enter phone number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="role"
                    label="Role"
                    rules={[{ required: true, message: "Please select role" }]}
                  >
                    <Select placeholder="Select role">
                      <Option value="admin">Admin</Option>
                      <Option value="renter">Renter</Option>
                      <Option value="supplier">Supplier</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* {!editingId && ( */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      { required: true, message: "Please enter password" },
                      {
                        min: 6,
                        message: "Password must be at least 6 characters",
                      },
                    ]}
                  >
                    <Input.Password placeholder="Enter password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "Please confirm password" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Passwords do not match")
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Confirm password" />
                  </Form.Item>
                </Col>
              </Row>
              {/* )} */}
            </TabPane>

            <TabPane tab="Additional Info" key="2">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="is_verified"
                    label="Verification Status"
                    initialValue={1}
                  >
                    <Select>
                      <Option value={1}>Verified</Option>
                      <Option value={0}>Not Verified</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="language_pref"
                    label="Language Preference"
                    initialValue="en"
                  >
                    <Select>
                      <Option value="en">English</Option>
                      <Option value="ar">Arabic</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Profile Picture">
                <Upload
                  name="profile_picture"
                  listType="picture-card"
                  fileList={fileList}
                  beforeUpload={beforeUpload}
                  onChange={handleChange}
                  onPreview={handlePreview}
                  maxCount={1}
                  accept="image/*"
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      <CameraOutlined style={{ fontSize: 20 }} />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
                <Modal
                  visible={previewVisible}
                  title="Profile Picture Preview"
                  footer={null}
                  onCancel={() => setPreviewVisible(false)}
                >
                  <img
                    alt="Preview"
                    style={{ width: "100%" }}
                    src={previewImage}
                  />
                </Modal>
              </Form.Item>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* User Detail Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <UserOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            <span>User Details</span>
          </div>
        }
        visible={userDetailVisible}
        onCancel={() => setUserDetailVisible(false)}
        width={700}
        footer={null}
      >
        {selectedUser && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Avatar
                src={selectedUser.profile_picture}
                icon={<UserOutlined />}
                size={120}
                style={{
                  marginBottom: 16,
                  border: "4px solid #f0f2f5",
                }}
              />
              <Title level={4} style={{ marginBottom: 4 }}>
                {selectedUser.name}
              </Title>
              <Text type="secondary">{selectedUser.email}</Text>
            </div>

            <Tabs defaultActiveKey="1">
              <TabPane tab="Basic Info" key="1">
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Role">
                    <Tag
                      color={
                        selectedUser.role === "admin"
                          ? "red"
                          : selectedUser.role === "supplier"
                          ? "orange"
                          : "blue"
                      }
                    >
                      {selectedUser.role}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {selectedUser.phone || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Badge
                      status={selectedUser.is_verified ? "success" : "error"}
                      text={
                        selectedUser.is_verified ? "Verified" : "Not Verified"
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Language Preference">
                    {selectedUser.language_pref === "ar" ? "Arabic" : "English"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Login">
                    {selectedUser.last_login
                      ? moment(selectedUser.last_login).format("lll")
                      : "Never logged in"}
                  </Descriptions.Item>
                </Descriptions>
              </TabPane>

              <TabPane tab="Activity" key="2">
                <div style={{ padding: 16 }}>
                  <Text type="secondary">
                    User activity log will appear here
                  </Text>
                </div>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserMaintenance;
